/**
 * Machine Edge Skills catalog — Live GET /edge/skills/catalog (+ status).
 * Soft≠Live: production never paints hardcoded warehouse or browser storage as Live.
 * Catalog rows = available skills only; Running/Standby stay on device caps report.
 */
import { path } from "./api.js";
import { isLocalHost } from "./pathseal.js";
import { EDGE_SKILL_WAREHOUSE } from "./edge-skills-warehouse.js";

export const EDGE_SKILL_CATALOG_STATUS_PATH = "/edge/skills/catalog/status";
export const EDGE_SKILL_INSTALL_REFUSAL =
  "This board is not ready to install skills yet";
export const EDGE_SKILL_REGISTRY_NOT_INSTALL =
  "Saving the list is not the same as the board having the skill";

/** Safe contract path with literal fallback when manifest lags. */
export function edgeSkillCatalogStatusPath() {
  try {
    return path("get__edge_skills_catalog_status");
  } catch {
    return EDGE_SKILL_CATALOG_STATUS_PATH;
  }
}

/** @param {{ deviceId?: string, boardClass?: string, kind?: string }} [opts] */
export function edgeSkillCatalogListPath(opts) {
  const o = opts && typeof opts === "object" ? opts : {};
  const query = {};
  const did = String(o.deviceId || "").trim();
  const bc = String(o.boardClass || "").trim();
  const kind = String(o.kind || "").trim();
  if (did) query.device_id = did;
  if (bc) query.board_class = bc;
  if (kind) query.kind = kind;
  try {
    return path("get__edge_skills_catalog", { query });
  } catch {
    const parts = Object.entries(query).map(
      ([k, v]) => encodeURIComponent(k) + "=" + encodeURIComponent(v),
    );
    return "/edge/skills/catalog" + (parts.length ? "?" + parts.join("&") : "");
  }
}

/** @param {unknown} data GET /edge/skills/catalog/status */
export function edgeSkillCatalogStatusViewOf(data) {
  const root = data && typeof data === "object" ? data : {};
  const productEnabled = root.product_enabled === true;
  return {
    ok: root.ok === true,
    productEnabled,
    listAllowed: root.list_allowed === true,
    writeAllowed: root.write_allowed === true,
    detail: String(root.detail || "").trim(),
    unavailable: !productEnabled,
    readOnly: true,
    softInventForbidden: root.soft_invent_forbidden !== false,
    sor: String(root.sor || root.catalog_table || "").trim(),
  };
}

function itemOf(raw) {
  if (!raw || typeof raw !== "object") return null;
  const skillId = String(raw.skill_id || "").trim();
  if (!skillId) return null;
  const caps = Array.isArray(raw.required_capabilities)
    ? raw.required_capabilities.map((c) => String(c).trim()).filter(Boolean)
    : [];
  return {
    id: skillId,
    skillId,
    label: String(raw.name || skillId).trim(),
    say: String(raw.description || raw.purpose || "").trim(),
    kind: String(raw.kind || "skill").trim(),
    version: String(raw.version || "").trim(),
    sizeKiB: Number(raw.size_kib) || 0,
    caps,
    requiredCapabilities: caps,
    enabled: raw.enabled !== false,
    compatibility: raw.compatibility && typeof raw.compatibility === "object"
      ? raw.compatibility
      : null,
    catalog: true,
    available: true,
  };
}

/** @param {unknown} data GET /edge/skills/catalog */
export function edgeSkillCatalogViewOf(data) {
  const root = data && typeof data === "object" ? data : {};
  const items = Array.isArray(root.items)
    ? root.items.map(itemOf).filter(Boolean)
    : [];
  return {
    items,
    count: Number.isFinite(Number(root.count)) ? Number(root.count) : items.length,
    persistence: String(root.persistence || "").trim(),
    honesty: String(root.honesty || "").trim(),
    writeSeat: root.write_seat === true,
    live: root.honesty === "live_pg" || items.length > 0,
  };
}

/** Localhost-only brochure shelf — never Live. */
export function edgeSkillCatalogSoftPreviewRows() {
  if (!isLocalHost()) return [];
  return EDGE_SKILL_WAREHOUSE.filter((sk) => sk.ota === "function").map((sk) => ({
    id: sk.id,
    skillId: sk.id,
    label: sk.label,
    say: sk.say,
    kind: "skill",
    version: sk.wave || "",
    sizeKiB: Number(sk.budgetKiB) || 0,
    caps: Array.isArray(sk.caps) ? sk.caps.slice() : [],
    requiredCapabilities: Array.isArray(sk.caps) ? sk.caps.slice() : [],
    enabled: true,
    compatibility: null,
    catalog: true,
    available: true,
    softPreview: true,
    honesty: "soft_preview",
  }));
}

/**
 * Resolve catalog rows for Machine surfaces.
 * @param {{
 *   statusView?: object,
 *   catalogView?: object,
 *   catalogError?: string,
 *   local?: boolean,
 * }} ctx
 */
export function resolveEdgeSkillCatalogRows(ctx) {
  const c = ctx && typeof ctx === "object" ? ctx : {};
  const status = c.statusView && typeof c.statusView === "object" ? c.statusView : {};
  const view = c.catalogView && typeof c.catalogView === "object" ? c.catalogView : {};
  const local = c.local === true;
  const err = String(c.catalogError || "").trim();

  if (status.listAllowed) {
    if (view.items && view.items.length) {
      return {
        rows: view.items,
        honesty: "live",
        label: "Live catalog",
        unavailable: false,
      };
    }
    if (!err) {
      return {
        rows: [],
        honesty: "live",
        label: "Live catalog · empty",
        unavailable: false,
      };
    }
  }

  if (local && (status.unavailable || err)) {
    const soft = edgeSkillCatalogSoftPreviewRows();
    if (soft.length) {
      return {
        rows: soft,
        honesty: "soft_preview",
        label: "Local catalog · localhost only",
        unavailable: false,
      };
    }
  }

  return {
    rows: [],
    honesty: "unavailable",
    label: status.detail || err || "Catalog unavailable · read-only",
    unavailable: true,
  };
}

/** Catalog skills not yet fully claimed — available to push, never Running. */
export function edgeSkillCatalogPushRows(catalogRows, capsView) {
  const claimed = capsView && capsView.claimedSet
    ? capsView.claimedSet
    : new Set((capsView && capsView.claimed) || []);
  return (catalogRows || []).filter((item) => {
    const caps = item.requiredCapabilities || item.caps || [];
    if (!caps.length) return true;
    return !caps.every((c) => claimed.has(c));
  });
}

/** Pure: claimed state for a catalog skill (for push band only). */
export function edgeSkillCatalogClaimStateOf(item, capsView) {
  const caps = (item && (item.requiredCapabilities || item.caps)) || [];
  const claimed = capsView && capsView.claimedSet
    ? capsView.claimedSet
    : new Set((capsView && capsView.claimed) || []);
  const verified = capsView && capsView.verifiedSet
    ? capsView.verifiedSet
    : new Set((capsView && capsView.verified) || []);
  if (!caps.length) {
    return { state: "capsule", label: "Capsule · pending bus", available: true };
  }
  const allVerified = caps.every((c) => verified.has(c));
  const allClaimed = caps.every((c) => claimed.has(c));
  if (allVerified) return { state: "on_device", label: "On device · see Running", available: false };
  if (allClaimed) return { state: "standby", label: "Standby · see above", available: false };
  return { state: "available", label: "Available · not on device", available: true };
}

/**
 * Runtime install/push allowed when product + device-specific device_ops_allowed.
 * Localhost preview never enables install without Live runtime ops.
 * @param {boolean} [_local] deprecated — kept for call-site compat
 * @param {object} [runtimeStatus] edgeSkillRuntimeStatusViewOf output
 * @param {object} [runtimeView] edgeSkillRuntimeDeviceViewOf output
 */
export function edgeSkillInstallAllowed(_local, runtimeStatus, runtimeView) {
  const s = runtimeStatus && typeof runtimeStatus === "object" ? runtimeStatus : {};
  const d = runtimeView && typeof runtimeView === "object" ? runtimeView : {};
  return s.productEnabled === true && d.deviceOpsAllowed === true;
}
